import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useListSettings } from "@/shared/composables/useListSettings";
// Bare resource-name key (mirrors "contact" / "organisation" / "donations") — see the
// BFF ListSettingsController allowlist. Must stay in sync with that entry (OP-6713).
const ENTITY = "users";
const DEFAULT_ORDER = ["name", "email", "roles", "environments", "state", "lastLogin"];
/**
 * Column management for the access-manager users list (OP-6713).
 *
 * Thin wrapper over the shared {@link useListSettings} (backend persistence + stale-key
 * reconciliation + locked-column re-assertion) — same shape as
 * `useAudienceContactsColumnSettings` — supplying only the users-specific column set.
 * `select` (the row checkbox column) is intentionally NOT listed here: the view
 * prepends it itself, same as Campaign/Organisation.
 */
export function useUsersListColumnSettings() {
    const { t } = useI18n();
    const settingsColumns = computed(() => [
        {
            key: "name",
            label: t("accessManager.users.columns.name"),
            visible: true,
            pinned: true,
            alwaysVisible: true,
        },
        {
            key: "email",
            label: t("accessManager.users.columns.email"),
            visible: true,
            pinned: false,
        },
        {
            key: "roles",
            label: t("accessManager.users.columns.roles"),
            visible: true,
            pinned: false,
        },
        {
            key: "environments",
            label: t("accessManager.users.columns.environments"),
            visible: true,
            pinned: false,
        },
        {
            key: "state",
            label: t("accessManager.users.columns.state"),
            visible: true,
            pinned: false,
        },
        {
            key: "lastLogin",
            label: t("accessManager.users.columns.lastLogin"),
            visible: true,
            pinned: false,
        },
    ]);
    const columnSettings = useListSettings({
        entity: ENTITY,
        // Getter rather than the ComputedRef itself: UseListSettingsOptions inherits `columns`
        // from UseColumnSettingsOptions, which declares it MaybeRefOrGetter, so either form is
        // accepted. A getter is reactive all the same: it re-reads t()/locale on each access.
        // (Nothing forces the choice; the brand mismatch this comment used to cite was a
        // duplicate-Vue artefact, gone with the single-Vue `paths` pin in tsconfig.json.)
        columns: () => settingsColumns.value,
        defaultOrder: DEFAULT_ORDER,
        defaultPinning: { left: ["name"], right: [] },
    });
    return { columnSettings };
}
